import React, { useEffect } from 'react';
import styles from 'src/styles/layout.module.css';
import MainLayout from '../../layouts/MainLayout';
import { useDispatch } from 'react-redux';
import { Link, NavLink, Redirect, Route, Switch, useParams } from 'react-router-dom';
import { Breadcrumb, Button, Space, Typography } from 'antd';
import { useDeviceGroupById } from 'src/features/deviceGroups/deviceGroups.hooks';
import { fetchDeviceGroup } from 'src/features/deviceGroups/deviceGroups.state';
import Loader from 'src/components/atoms/Loader';
import { DeviceGroup } from 'src/features/deviceGroups/deviceGroups.types';
import { RoleEnum } from 'src/features/users/users.types';
import localStyles from 'src/components/pages/Company/CompanyPage/CompanyPage.module.css';
import { AppstoreOutlined, PictureOutlined, ThunderboltTwoTone, VideoCameraOutlined } from '@ant-design/icons';
import RouteProtected from 'src/components/blocks/RouteProtected/RouteProtected';
import { Content } from 'antd/es/layout/layout';
import DeviceGroupDevicesTab from 'src/components/pages/DeviceGroupPage/DeviceGroupDevicesTab';
import DeviceGroupBannersTab from 'src/components/pages/DeviceGroupPage/DeviceGroupBannersTab';
import DeviceGroupVideosTab from 'src/components/pages/DeviceGroupPage/DeviceGroupVideosTab';
import DeviceGroupScreensTab from 'src/components/pages/DeviceGroupPage/DeviceGroupScreensTab';
import DeviceGroupFirmwaresTab from 'src/components/pages/DeviceGroupPage/DeviceGroupFirmwaresTab';

const DeviceGroupPage: React.FC = () => {
  const { groupId } = useParams() as { groupId: string };
  const group = useDeviceGroupById(groupId) as DeviceGroup;

  return (
    <MainLayout>
      <div>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/testing/groups'>
                <Space>Группа устройств</Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to='/testing/groups'>
                <Space>{group.name}</Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Space direction={'vertical'} size={20}>
            <Space direction={'vertical'} size={0}>
              <Typography.Title level={2} className={styles.title} style={{ marginBottom: 0 }}>
                {group.name}
              </Typography.Title>
              <Space>
                <Typography.Text>Шаблон: </Typography.Text>
                {group.templateId ? (
                  <Link to={`/content/templates/${group.templateId}/groups`}>{group.templateName}</Link>
                ) : (
                  '-'
                )}
              </Space>
            </Space>
            <Space size='small' wrap>
              <NavLink to={`devices`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <ThunderboltTwoTone />
                  Девайсы
                </Button>
              </NavLink>
              <NavLink to={`videos`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <VideoCameraOutlined />
                  Видео
                </Button>
              </NavLink>
              <NavLink to={`banners`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <PictureOutlined />
                  Баннеры
                </Button>
              </NavLink>
              <NavLink to={`screens`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <AppstoreOutlined />
                  Экраны
                </Button>
              </NavLink>
              <NavLink to={`firmwares`} activeClassName={localStyles.activeLink}>
                <Button size='middle'>
                  <AppstoreOutlined />
                  Прошивки
                </Button>
              </NavLink>
            </Space>
          </Space>
          <Content>
            <Route
              render={({ match: { path } }) => (
                <Switch>
                  <RouteProtected
                    exact
                    path={`${path}/screens`}
                    component={DeviceGroupScreensTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/banners`}
                    component={DeviceGroupBannersTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/videos`}
                    component={DeviceGroupVideosTab}
                    roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/devices`}
                    component={DeviceGroupDevicesTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <RouteProtected
                    exact
                    path={`${path}/firmwares`}
                    component={DeviceGroupFirmwaresTab}
                    roles={[RoleEnum.admin, RoleEnum.observer]}
                  />
                  <Redirect from={path} to={`${path}/devices`} exact />
                </Switch>
              )}
            />
          </Content>
        </div>
      </div>
    </MainLayout>
  );
};

const DeviceGroupsPageBlock: React.FC = () => {
  const dispatch = useDispatch();
  const { groupId } = useParams() as { groupId: string };
  const group = useDeviceGroupById(groupId);

  useEffect(() => {
    dispatch(fetchDeviceGroup({ groupId }));
  }, [dispatch]);

  if (!group) {
    return <Loader />;
  }

  return <DeviceGroupPage />;
};

export default DeviceGroupsPageBlock;
